import { isOfficialReferenceEvent } from '@/components/review/officialSource';
import type {
  ConsolidatedEventCluster,
  EventResponse,
  SessionEvent,
  UserAssertion,
} from '@/types';
import { formatDecisionSummary, getDecisionLabel } from '@/types';

export type DecisionComponentKey =
  | 'onFieldBall'
  | 'onFieldSanction'
  | 'judgement'
  | 'reviewedBall'
  | 'reviewedSanction';

export type DecisionComponentMatch = Record<DecisionComponentKey, boolean>;

/** Components averaged for 完整共識率 / alignment scores vs official. */
export const REVIEW_CONSENSUS_COMPONENT_KEYS = [
  'judgement',
  'reviewedBall',
  'reviewedSanction',
] as const satisfies readonly DecisionComponentKey[];

type ReviewConsensusComponentKey = (typeof REVIEW_CONSENSUS_COMPONENT_KEYS)[number];

const REVIEW_CONSENSUS_COMPONENT_COUNT = REVIEW_CONSENSUS_COMPONENT_KEYS.length;

/** Max gap (seconds) between consecutive marks to treat as the same incident */
const CLUSTER_WINDOW_SECONDS = 8;

/** Same ball on-field decision for clustering (legacy kickoff → play_on). */
export function normalizeClusterBallDecision(value?: string | null): string {
  const v = (value || '').trim();
  if (v === 'kickoff') return 'play_on';
  return v;
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[mid];
  return (sorted[mid - 1] + sorted[mid]) / 2;
}

function average(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

function modeOf(values: string[]): string {
  const counter = new Map<string, number>();
  let best = '';
  let bestCount = -1;
  for (const value of values) {
    const next = (counter.get(value) || 0) + 1;
    counter.set(value, next);
    if (next > bestCount) {
      bestCount = next;
      best = value;
    }
  }
  return best;
}

/** Treat empty punishment as 無罰則 for comparison */
export function normalizeSanction(value?: string | null): string {
  return value && value.trim() ? value : 'no_penalty';
}

export function normalizeJudgement(value?: string | null): UserAssertion['judgement'] {
  if (!value) return 'pending';
  if (value === 'correct') return 'correct';
  if (value === 'wrong' || value === 'incorrect') return 'incorrect';
  if (value === 'uncertain') return 'uncertain';
  return 'pending';
}

export type DecisionSignatureInput = {
  onFieldBall: string;
  onFieldSanction: string;
  judgement?: UserAssertion['judgement'];
  reviewedBall?: string | null;
  reviewedSanction?: string | null;
};

/** Effective outcome each user believes is correct — used for consensus matching */
export function buildDecisionSignature(input: DecisionSignatureInput): string {
  const onBall = normalizeClusterBallDecision(input.onFieldBall);
  const onSanction = normalizeSanction(input.onFieldSanction);
  const judgement = input.judgement || 'pending';

  if (judgement === 'correct') {
    return `ef:${onBall}|${onSanction}|j:correct`;
  }

  if (judgement === 'incorrect') {
    const reviewedBall = normalizeClusterBallDecision(input.reviewedBall);
    const reviewedSanction = normalizeSanction(input.reviewedSanction);
    return `ef:${reviewedBall}|${reviewedSanction}|j:incorrect|of:${onBall}|${onSanction}`;
  }

  return `of:${onBall}|${onSanction}|j:${judgement}`;
}

export function assertionDecisionSignature(assertion: UserAssertion): string {
  return buildDecisionSignature({
    onFieldBall: assertion.onFieldBall,
    onFieldSanction: assertion.onFieldSanction,
    judgement: assertion.judgement,
    reviewedBall: assertion.reviewedBall,
    reviewedSanction: assertion.reviewedSanction,
  });
}

/** Human-readable effective decision for display */
export function formatAssertionOutcome(assertion: UserAssertion): string {
  const judgement = assertion.judgement || 'pending';
  if (judgement === 'correct') {
    return formatDecisionSummary(assertion.onFieldBall, normalizeSanction(assertion.onFieldSanction));
  }
  if (judgement === 'incorrect') {
    const reviewed = formatDecisionSummary(
      assertion.reviewedBall,
      normalizeSanction(assertion.reviewedSanction)
    );
    if (reviewed && reviewed !== '—') return reviewed;
  }
  return formatDecisionSummary(assertion.onFieldBall, normalizeSanction(assertion.onFieldSanction));
}

export function formatAssertionOnField(assertion: UserAssertion): string {
  const sanction = normalizeSanction(assertion.onFieldSanction);
  return formatDecisionSummary(assertion.onFieldBall, sanction === 'no_penalty' ? null : sanction);
}

export function formatAssertionReviewed(assertion: UserAssertion): string | null {
  if (assertion.judgement === 'correct') return null;
  const reviewed = formatDecisionSummary(assertion.reviewedBall, normalizeSanction(assertion.reviewedSanction));
  return reviewed && reviewed !== '—' ? reviewed : null;
}

/** Ball + sanction backing `formatAssertionOutcome` (same fallback rules). */
export function effectiveOutcomeParts(assertion: UserAssertion): {
  ball: string | null;
  sanction: string | null;
} {
  const onField = {
    ball: assertion.onFieldBall ?? null,
    sanction: assertion.onFieldSanction ?? null,
  };
  if (assertion.judgement === 'incorrect') {
    const reviewed = formatDecisionSummary(
      assertion.reviewedBall,
      normalizeSanction(assertion.reviewedSanction)
    );
    if (reviewed && reviewed !== '—') {
      return {
        ball: assertion.reviewedBall ?? null,
        sanction: assertion.reviewedSanction ?? null,
      };
    }
  }
  return onField;
}

export function normalizeOfficialUserIds(
  ids?: number | number[]
): number[] | undefined {
  if (ids == null) return undefined;
  if (Array.isArray(ids)) return ids.length ? ids.map(Number) : undefined;
  return [Number(ids)];
}

function isOfficialUserId(
  userId: number | string,
  officialUserIds?: number | number[]
): boolean {
  const ids = normalizeOfficialUserIds(officialUserIds);
  if (!ids?.length) return false;
  return ids.some((id) => Number(id) === Number(userId));
}

export function isAssertionAlignedWithOfficials(
  assertion: UserAssertion,
  officials: UserAssertion[]
): boolean {
  if (officials.length === 0) return false;
  const sig = assertionDecisionSignature(assertion);
  return officials.some((o) => assertionDecisionSignature(o) === sig);
}

export function reviewedDiffAgainstOfficials(
  assertion: UserAssertion,
  officials: UserAssertion[],
  highlightDiff: boolean
): { ball: boolean; sanction: boolean } {
  if (!highlightDiff || officials.length === 0) return { ball: false, sanction: false };
  if (isAssertionAlignedWithOfficials(assertion, officials)) {
    return { ball: false, sanction: false };
  }
  return outcomePartsDiffer(assertion, officials[0]);
}

export function outcomePartsDiffer(
  participant: UserAssertion,
  official: UserAssertion
): { ball: boolean; sanction: boolean } {
  const a = effectiveOutcomeParts(participant);
  const b = effectiveOutcomeParts(official);
  return {
    ball: normalizeClusterBallDecision(a.ball) !== normalizeClusterBallDecision(b.ball),
    sanction: normalizeSanction(a.sanction) !== normalizeSanction(b.sanction),
  };
}

export function reviewedBallLabel(assertion: UserAssertion): string {
  const ball = effectiveOutcomeParts(assertion).ball;
  return ball ? getDecisionLabel(ball) : '—';
}

export function reviewedSanctionLabel(assertion: UserAssertion): string {
  const sanction = effectiveOutcomeParts(assertion).sanction;
  const key = sanction ? normalizeSanction(sanction) : 'no_penalty';
  return key !== 'no_penalty' ? getDecisionLabel(key) : '無罰則';
}

export function assertionComponentsMatch(a: UserAssertion, b: UserAssertion): DecisionComponentMatch {
  const aRev = effectiveOutcomeParts(a);
  const bRev = effectiveOutcomeParts(b);
  return {
    onFieldBall:
      normalizeClusterBallDecision(a.onFieldBall) === normalizeClusterBallDecision(b.onFieldBall),
    onFieldSanction: normalizeSanction(a.onFieldSanction) === normalizeSanction(b.onFieldSanction),
    judgement: (a.judgement || 'pending') === (b.judgement || 'pending'),
    reviewedBall: normalizeClusterBallDecision(aRev.ball) === normalizeClusterBallDecision(bRev.ball),
    reviewedSanction: normalizeSanction(aRev.sanction) === normalizeSanction(bRev.sanction),
  };
}

/** 0–100: share of review components (評價、覆核球、覆核罰則) matching the reference. */
export function assertionAlignmentScore(a: UserAssertion, b: UserAssertion): number {
  const match = assertionComponentsMatch(a, b);
  const aligned = REVIEW_CONSENSUS_COMPONENT_KEYS.filter((k) => match[k]).length;
  return (aligned / REVIEW_CONSENSUS_COMPONENT_COUNT) * 100;
}

function modeAgreementRate<T>(assertions: UserAssertion[], pick: (a: UserAssertion) => T): number {
  if (assertions.length === 0) return 0;
  const map = new Map<string, number>();
  for (const a of assertions) {
    const key = String(pick(a));
    map.set(key, (map.get(key) || 0) + 1);
  }
  const best = Math.max(...map.values(), 0);
  return (best / assertions.length) * 100;
}

function computeComponentModeAgreement(assertions: UserAssertion[]): number {
  if (!assertions.length) return 0;
  const rates = [
    modeAgreementRate(assertions, (a) => a.judgement || 'pending'),
    modeAgreementRate(assertions, (a) =>
      normalizeClusterBallDecision(effectiveOutcomeParts(a).ball)
    ),
    modeAgreementRate(assertions, (a) => normalizeSanction(effectiveOutcomeParts(a).sanction)),
  ];
  return rates.reduce((sum, r) => sum + r, 0) / REVIEW_CONSENSUS_COMPONENT_COUNT;
}

/** Tooltip copy for 共識% (comparison table, integrated timeline, analytics). */
export const CONSENSUS_RATE_TOOLTIP =
  '共識% 計算方式：' +
  '有指定官方時，取各參與者判決，以覆核判決評價、覆核球權、覆核罰則三項與官方比對（相符項 ÷ 3 × 100）；' +
  '若有多位官方，每位參與者取與任一位官方吻合度最高者計分，再對所有參與者取平均。' +
  '尚無官方時，以全部標記在三項上各自多数決的一致比例再取平均。' +
  '全局共識率為各事件一致%／共識% 的平均（不含尚無參與者回覆的官方事件；有官方時顯示一致%，無官方時顯示共識%）。';

/** Best 0–100 alignment of one mark vs any designated official reference. */
export function participantAlignmentScoreVsOfficials(
  assertion: UserAssertion,
  officials: UserAssertion[]
): number {
  if (officials.length === 0) return 0;
  return Math.max(...officials.map((o) => assertionAlignmentScore(assertion, o)));
}

/**
 * Unified per-event 共識% (comparison table, timeline, analytics).
 * Returns null when officials exist but no participant marks yet.
 */
export function computeClusterConsensusRate(
  cluster: ConsolidatedEventCluster,
  officialUserIds?: number | number[]
): number | null {
  const officials = getClusterOfficialAssertions(cluster, officialUserIds);
  if (officials.length > 0) {
    const officialUserIdSet = new Set(officials.map((o) => String(o.userId)));
    const participants = cluster.assertions.filter(
      (a) => a.role === 'participant' && !officialUserIdSet.has(String(a.userId))
    );
    if (participants.length === 0) return null;
    return (
      participants.reduce(
        (sum, a) => sum + participantAlignmentScoreVsOfficials(a, officials),
        0
      ) / participants.length
    );
  }
  if (cluster.assertions.length === 0) return null;
  return computeComponentModeAgreement(cluster.assertions);
}

export function formatClusterConsensusRateLabel(
  rate: number | null | undefined,
  hasOfficialReference = false
): string {
  if (rate == null) return '—';
  return hasOfficialReference
    ? `${Math.round(rate)}% 一致`
    : `${Math.round(rate)}% 共識`;
}

/** Event chip label: 一致% when official reference exists, else 共識%. */
export function formatClusterAgreementDisplayLabel(
  cluster: ConsolidatedEventCluster,
  officialUserIds?: number | number[]
): string {
  const hasOfficial = getClusterOfficialAssertions(cluster, officialUserIds).length > 0;
  return formatClusterConsensusRateLabel(cluster.agreementRate, hasOfficial);
}

export function computeClusterAgreementRate(
  cluster: ConsolidatedEventCluster,
  officialUserIds?: number | number[]
): number | null {
  return computeClusterConsensusRate(cluster, officialUserIds);
}

function getClusterReferenceAssertion(
  cluster: ConsolidatedEventCluster,
  officialUserId?: number
): UserAssertion | null {
  const owner = getClusterOwnerAssertion(cluster, officialUserId);
  if (owner) return owner;
  const sig = cluster.dominantSignature;
  if (!sig) return cluster.assertions[0] ?? null;
  return cluster.assertions.find((a) => assertionDecisionSignature(a) === sig) ?? cluster.assertions[0] ?? null;
}

export function formatJudgementLabel(judgement?: UserAssertion['judgement']): string {
  if (judgement === 'correct') return '正確';
  if (judgement === 'incorrect') return '錯誤';
  if (judgement === 'uncertain') return '灰色';
  return '—';
}

export function agreementMetricColor(
  rate: number,
  dark = false
): { bg: string; color: string; border: string } {
  if (dark) {
    if (rate >= 80) {
      return { bg: 'rgba(46,125,50,0.32)', color: '#a5d6a7', border: 'rgba(129,199,132,0.55)' };
    }
    if (rate >= 60) {
      return { bg: 'rgba(144,164,174,0.28)', color: '#cfd8dc', border: 'rgba(176,190,197,0.5)' };
    }
    return { bg: 'rgba(255,152,0,0.28)', color: '#ffcc80', border: 'rgba(255,183,77,0.55)' };
  }
  if (rate >= 80) return { bg: 'rgba(46,125,50,0.10)', color: '#2e7d32', border: 'rgba(46,125,50,0.25)' };
  if (rate >= 60) return { bg: 'rgba(84,110,122,0.12)', color: '#546e7a', border: 'rgba(84,110,122,0.28)' };
  return { bg: 'rgba(168,67,0,0.12)', color: '#a84300', border: 'rgba(168,67,0,0.28)' };
}

function fallbackUserName(
  role: 'owner' | 'participant',
  userId: number | string,
  label?: string | null
): string {
  if (label && label.trim()) return label.trim();
  return role === 'owner' ? `建立者 #${userId}` : `參與者 #${userId}`;
}

type InternalCluster = {
  clusterId: string;
  assertions: UserAssertion[];
  medianTimestamp: number;
};

/** One row per user per cluster — prefer own event mark over response on the same play. */
function pickAssertionForUser(existing: UserAssertion, incoming: UserAssertion): UserAssertion {
  if (existing.source === 'event' && incoming.source === 'response') return existing;
  if (existing.source === 'response' && incoming.source === 'event') return incoming;
  return incoming.timestamp >= existing.timestamp ? incoming : existing;
}

function dedupeAssertionsByUser(assertions: UserAssertion[]): UserAssertion[] {
  const byUser = new Map<string, UserAssertion>();
  for (const a of assertions) {
    const key = String(a.userId);
    const prev = byUser.get(key);
    byUser.set(key, prev ? pickAssertionForUser(prev, a) : a);
  }
  return [...byUser.values()].sort((x, y) => x.timestamp - y.timestamp);
}

function finalizeTimeCluster(assertions: UserAssertion[], index: number): InternalCluster {
  const unique = dedupeAssertionsByUser(assertions);
  const timestamps = unique.map((x) => x.timestamp);
  return {
    clusterId: `cluster-${timestamps[0].toFixed(3)}-${index + 1}`,
    assertions: unique,
    medianTimestamp: median(timestamps),
  };
}

/** Group marks within time window and matching on-field ball decision. */
function clusterByTimeAndBall(assertions: UserAssertion[]): InternalCluster[] {
  const sorted = [...assertions].sort((a, b) => a.timestamp - b.timestamp);
  if (sorted.length === 0) return [];

  const clusters: InternalCluster[] = [];
  let group: UserAssertion[] = [sorted[0]];

  for (let i = 1; i < sorted.length; i += 1) {
    const prev = group[group.length - 1];
    const next = sorted[i];
    const sameBall =
      normalizeClusterBallDecision(next.onFieldBall) ===
      normalizeClusterBallDecision(prev.onFieldBall);
    if (next.timestamp - prev.timestamp <= CLUSTER_WINDOW_SECONDS && sameBall) {
      group.push(next);
    } else {
      clusters.push(finalizeTimeCluster(group, clusters.length));
      group = [next];
    }
  }

  clusters.push(finalizeTimeCluster(group, clusters.length));
  return clusters;
}

function findClosestAssertion(assertions: UserAssertion[], pivot: number): UserAssertion | null {
  if (assertions.length === 0) return null;
  let closest = assertions[0];
  let bestDist = Math.abs(assertions[0].timestamp - pivot);
  for (let i = 1; i < assertions.length; i += 1) {
    const dist = Math.abs(assertions[i].timestamp - pivot);
    if (dist < bestDist) {
      bestDist = dist;
      closest = assertions[i];
    }
  }
  return closest;
}

/** Display/cluster anchor: official mark time if present, else earliest mark in cluster. */
function resolveClusterAnchor(
  assertions: UserAssertion[],
  officialUserIds?: number | number[]
): { anchorTimestamp: number; ownerAnchor: UserAssertion | null } {
  if (assertions.length === 0) {
    return { anchorTimestamp: 0, ownerAnchor: null };
  }
  const ids = normalizeOfficialUserIds(officialUserIds);
  const ownerPool = ids?.length
    ? assertions.filter((a) => isOfficialUserId(a.userId, ids))
    : assertions.filter((a) => a.role === 'owner');
  if (ownerPool.length > 0) {
    const ownerAnchor = ownerPool.reduce((earliest, a) =>
      a.timestamp < earliest.timestamp ? a : earliest
    );
    return { anchorTimestamp: ownerAnchor.timestamp, ownerAnchor };
  }
  const earliest = assertions.reduce((best, a) => (a.timestamp < best.timestamp ? a : best));
  return { anchorTimestamp: earliest.timestamp, ownerAnchor: null };
}

function officialReferencePool(
  cluster: ConsolidatedEventCluster,
  officialUserIds?: number | number[]
): UserAssertion[] {
  const ids = normalizeOfficialUserIds(officialUserIds);
  if (ids?.length) {
    return cluster.assertions.filter((a) => isOfficialUserId(a.userId, ids));
  }
  return cluster.assertions.filter((a) => a.role === 'owner');
}

/** One assertion per designated official source in a cluster. */
export function getClusterOfficialAssertions(
  cluster: ConsolidatedEventCluster,
  officialUserIds?: number | number[]
): UserAssertion[] {
  const ids = normalizeOfficialUserIds(officialUserIds);
  if (!ids?.length) {
    const one = findClosestAssertion(
      officialReferencePool(cluster, undefined),
      cluster.anchorTimestamp
    );
    return one ? [one] : [];
  }
  return ids
    .map((id) => {
      const pool = cluster.assertions.filter((a) => Number(a.userId) === Number(id));
      return findClosestAssertion(pool, cluster.anchorTimestamp);
    })
    .filter((a): a is UserAssertion => a != null);
}

export function getClusterAssertionForUser(
  cluster: ConsolidatedEventCluster,
  userId: number | string
): UserAssertion | null {
  const pool = cluster.assertions.filter((a) => Number(a.userId) === Number(userId));
  return findClosestAssertion(pool, cluster.anchorTimestamp);
}

/** Primary official assertion (first designated source) — backward compatible. */
export function getClusterOwnerAssertion(
  cluster: ConsolidatedEventCluster,
  officialUserId?: number | number[]
): UserAssertion | null {
  return getClusterOfficialAssertions(cluster, officialUserId)[0] ?? null;
}

/** Assertions listed in event stats dialog「作答」table (all marks except the official reference). */
export function getClusterAnswerTableAssertions(
  cluster: ConsolidatedEventCluster,
  officialUserId?: number | number[]
): UserAssertion[] {
  const officials = getClusterOfficialAssertions(cluster, officialUserId);
  if (officials.length === 0) return cluster.assertions;
  const officialIds = new Set(officials.map((o) => o.assertion_id));
  return cluster.assertions.filter((a) => !officialIds.has(a.assertion_id));
}

function assertionHasOfficialAnswer(assertion: UserAssertion): boolean {
  return !!(
    assertion.onFieldBall?.trim() ||
    assertion.judgement === 'correct' ||
    assertion.judgement === 'incorrect' ||
    assertion.judgement === 'uncertain'
  );
}

/** Whether any designated official source has marked this cluster (tick vs groups icon). */
export function clusterHasOfficialAnswer(
  cluster: ConsolidatedEventCluster,
  officialUserId?: number | number[]
): boolean {
  return getClusterOfficialAssertions(cluster, officialUserId).some(assertionHasOfficialAnswer);
}

export function assertionLinkedEventId(assertion: UserAssertion): string | null {
  return assertion.linked_event_id ?? (assertion.source === 'event' ? assertion.source_id : null) ?? null;
}

export function participantAgreementWithOwner(
  cluster: ConsolidatedEventCluster,
  officialUserIds?: number | number[]
): number | null {
  return computeClusterConsensusRate(cluster, officialUserIds);
}

function buildAssertionFromEvent(
  event: SessionEvent,
  role: 'owner' | 'participant'
): UserAssertion | null {
  if (event.video_second == null) return null;
  if (!event.on_field_decision && !event.judgement) return null;
  const userName = fallbackUserName(
    role,
    event.created_by_user_id,
    event.user?.full_name || event.user?.reference_no || null
  );
  return {
    assertion_id: `event:${event.id}`,
    source: 'event',
    source_id: event.id,
    linked_event_id: event.id,
    userId: String(event.created_by_user_id),
    userName,
    photoPath: event.user?.photo_path ?? null,
    role,
    timestamp: Number(event.video_second),
    onFieldBall: event.on_field_decision || '',
    onFieldSanction: normalizeSanction(event.on_field_punishment),
    reviewedBall: event.reviewed_decision || '',
    reviewedSanction: normalizeSanction(event.reviewed_punishment),
    judgement: normalizeJudgement(event.judgement),
    notes: event.note || undefined,
  };
}

function isOfficialSourceMark(event: SessionEvent, officialUserIds?: number | number[]): boolean {
  const ids = normalizeOfficialUserIds(officialUserIds);
  if (ids?.length) {
    return ids.some((id) => Number(event.created_by_user_id) === Number(id));
  }
  return event.marker_role !== 'participant';
}

export function buildConsensusClusters(
  events: SessionEvent[],
  responses: EventResponse[],
  opts?: { ownerUserId?: number; ownerUserIds?: number[] }
): ConsolidatedEventCluster[] {
  const officialById = new Map<string, SessionEvent>();
  const ownerUserIds =
    opts?.ownerUserIds?.length
      ? opts.ownerUserIds
      : opts?.ownerUserId != null
        ? [opts.ownerUserId]
        : undefined;
  for (const event of events) {
    if (isOfficialSourceMark(event, ownerUserIds)) officialById.set(event.id, event);
  }

  const assertions: UserAssertion[] = [];
  for (const event of events) {
    if (event.video_second == null || (!event.on_field_decision && !event.judgement)) continue;
    const role: 'owner' | 'participant' = isOfficialSourceMark(event, ownerUserIds)
      ? 'owner'
      : 'participant';
    const assertion = buildAssertionFromEvent(event, role);
    if (assertion) assertions.push(assertion);
  }

  for (const response of responses) {
    if (!response.on_field_decision && !response.judgement) continue;
    const official = officialById.get(response.official_event_id);
    if (!official || official.video_second == null) continue;
    const role: 'owner' | 'participant' =
      ownerUserIds?.length
        ? ownerUserIds.some((id) => Number(response.user_id) === Number(id))
          ? 'owner'
          : 'participant'
        : Number(response.user_id) === Number(official.created_by_user_id)
          ? 'owner'
          : 'participant';
    const userName = fallbackUserName(
      role,
      response.user_id,
      response.user?.full_name || response.user?.reference_no || null
    );
    assertions.push({
      assertion_id: `response:${response.id}`,
      source: 'response',
      source_id: response.id,
      linked_event_id: response.official_event_id,
      userId: String(response.user_id),
      userName,
      photoPath: response.user?.photo_path ?? null,
      role,
      timestamp: Number(official.video_second),
      onFieldBall: response.on_field_decision || '',
      onFieldSanction: normalizeSanction(response.on_field_punishment),
      reviewedBall: response.reviewed_decision || '',
      reviewedSanction: normalizeSanction(response.reviewed_punishment),
      judgement: normalizeJudgement(response.judgement),
      notes: response.note || undefined,
    });
  }

  const clustered = clusterByTimeAndBall(assertions);

  return clustered
    .map<ConsolidatedEventCluster>((cluster, index) => {
      const { anchorTimestamp, ownerAnchor } = resolveClusterAnchor(
        cluster.assertions,
        ownerUserIds
      );

      const signatures = cluster.assertions.map(assertionDecisionSignature);
      const dominantSignature = modeOf(signatures);
      const fullAgreementRate = computeComponentModeAgreement(cluster.assertions);

      const dominantBall = modeOf(
        cluster.assertions.map((x) => normalizeClusterBallDecision(x.onFieldBall))
      );
      const dominantSanction = modeOf(cluster.assertions.map((x) => normalizeSanction(x.onFieldSanction)));
      const ownerLinkedOfficial =
        ownerAnchor?.linked_event_id && officialById.has(ownerAnchor.linked_event_id)
          ? ownerAnchor.linked_event_id
          : null;
      const officialCandidates = cluster.assertions
        .map((x) => x.linked_event_id || null)
        .filter((id): id is string => !!id && officialById.has(id));
      const closestOfficial = officialCandidates
        .map((id) => {
          const evt = officialById.get(id);
          const time = Number(evt?.video_second ?? 0);
          return { id, dist: Math.abs(time - anchorTimestamp) };
        })
        .sort((a, b) => a.dist - b.dist)[0]?.id;
      const anchorEventId =
        ownerLinkedOfficial ||
        closestOfficial ||
        cluster.assertions.find((x) => x.linked_event_id)?.linked_event_id ||
        null;

      const result: ConsolidatedEventCluster = {
        clusterId: `consensus-${index + 1}`,
        anchorTimestamp,
        onFieldBall: dominantBall,
        assertions: cluster.assertions.sort((a, b) => a.timestamp - b.timestamp),
        dominantSanction: dominantSanction || '',
        fullAgreementRate,
        dominantSignature,
        averageVariance: average(cluster.assertions.map((x) => Math.abs(x.timestamp - anchorTimestamp))),
        anchorEventId,
      };
      result.agreementRate = computeClusterAgreementRate(result, ownerUserIds) ?? undefined;
      return result;
    })
    .sort((a, b) => a.anchorTimestamp - b.anchorTimestamp);
}

export type EventEditPayload = {
  on_field_decision?: string | null;
  on_field_punishment?: string | null;
  reviewed_decision?: string | null;
  reviewed_punishment?: string | null;
  judgement?: string | null;
  note?: string | null;
  video_second?: number;
};

/** Recompute cluster metrics after editing a linked session event (optimistic UI). */
export function refreshClusterAfterEventEdit(
  cluster: ConsolidatedEventCluster,
  eventId: string,
  payload: EventEditPayload,
  officialUserIds?: number | number[]
): ConsolidatedEventCluster {
  const assertions = cluster.assertions
    .map((a): UserAssertion => {
      const linked = assertionLinkedEventId(a) ?? (a.source === 'event' ? a.source_id : null);
      if (linked !== eventId || a.source !== 'event') return a;
      return {
        ...a,
        timestamp: payload.video_second ?? a.timestamp,
        onFieldBall: payload.on_field_decision || '',
        onFieldSanction: normalizeSanction(payload.on_field_punishment ?? a.onFieldSanction),
        reviewedBall: payload.reviewed_decision || '',
        reviewedSanction: normalizeSanction(payload.reviewed_punishment ?? a.reviewedSanction),
        judgement: normalizeJudgement(payload.judgement),
        notes: payload.note?.trim() ? payload.note : undefined,
      };
    })
    .sort((a, b) => a.timestamp - b.timestamp);

  const { anchorTimestamp } = resolveClusterAnchor(assertions, officialUserIds);

  const signatures = assertions.map(assertionDecisionSignature);
  const dominantSignature = modeOf(signatures);
  const dominantBall = modeOf(
    assertions.map((x) => normalizeClusterBallDecision(x.onFieldBall))
  );
  const dominantSanction = modeOf(assertions.map((x) => normalizeSanction(x.onFieldSanction)));

  const next: ConsolidatedEventCluster = {
    ...cluster,
    assertions,
    anchorTimestamp,
    onFieldBall: dominantBall,
    dominantSanction: dominantSanction || '',
    fullAgreementRate: computeComponentModeAgreement(assertions),
    dominantSignature,
    averageVariance: average(assertions.map((x) => Math.abs(x.timestamp - anchorTimestamp))),
  };
  next.agreementRate = computeClusterAgreementRate(next, officialUserIds) ?? undefined;
  return next;
}

/** Cluster id containing an event (for timeline/list selection after creating a mark). */
export function findClusterIdForEventId(
  clusters: ConsolidatedEventCluster[],
  eventId: string
): string | null {
  for (const cluster of clusters) {
    if (cluster.anchorEventId === eventId) return cluster.clusterId;
    if (
      cluster.assertions.some((a) => a.linked_event_id === eventId || a.source_id === eventId)
    ) {
      return cluster.clusterId;
    }
  }
  return null;
}

/** Id to select in timeline/list after inserting a mark. */
export function resolveMarkSelectionId(
  event: SessionEvent,
  events: SessionEvent[],
  responses: EventResponse[],
  options: { useConsensusSelection: boolean; officialUserId?: number }
): string {
  if (!options.useConsensusSelection) return event.id;
  const clusters = buildConsensusClusters(events, responses, {
    ownerUserId: options.officialUserId,
  });
  return findClusterIdForEventId(clusters, event.id) ?? event.id;
}

export type TimelineMarkerIcon = 'verified' | 'groups' | 'person';

/** Icon for horizontal timeline markers (fullscreen / video shell). */
export function getTimelineMarkerIcon(
  event: SessionEvent,
  options: {
    clusterById?: Map<string, ConsolidatedEventCluster>;
    officialUserId?: number;
  }
): TimelineMarkerIcon | null {
  const cluster = options.clusterById?.get(event.id);
  if (cluster) {
    return clusterHasOfficialAnswer(cluster, options.officialUserId) ? 'verified' : 'groups';
  }
  if (isOfficialReferenceEvent(event, options.officialUserId)) return 'verified';
  return 'person';
}

export function buildClusterTimelineEvents(
  clusters: ConsolidatedEventCluster[],
  officialUserId?: number
): SessionEvent[] {
  return clusters.map((cluster, idx) => {
    const owner = getClusterOwnerAssertion(cluster, officialUserId);
    const judgement = owner?.judgement;
    return {
      id: cluster.clusterId,
      session_id: 'consensus',
      session_type: 'review',
      event_type: 'decision',
      marker_role: 'official',
      video_second: cluster.anchorTimestamp,
      on_field_decision: cluster.onFieldBall,
      on_field_punishment: cluster.dominantSanction || null,
      reviewed_decision: null,
      reviewed_punishment: null,
      judgement: judgement && judgement !== 'pending' ? judgement : null,
      created_by_user_id: 0,
      created_at: `${idx}`,
      updated_at: `${idx}`,
    };
  });
}

export function consensusDistributions(cluster: ConsolidatedEventCluster) {
  const ballMap = new Map<string, number>();
  const sanctionMap = new Map<string, number>();
  const judgementMap = new Map<string, number>();
  const reviewedBallMap = new Map<string, number>();
  const reviewedSanctionMap = new Map<string, number>();
  const effectiveMap = new Map<string, number>();
  const signatureMap = new Map<string, number>();

  for (const assertion of cluster.assertions) {
    ballMap.set(assertion.onFieldBall || '—', (ballMap.get(assertion.onFieldBall || '—') || 0) + 1);
    const sanction = normalizeSanction(assertion.onFieldSanction);
    sanctionMap.set(sanction, (sanctionMap.get(sanction) || 0) + 1);
    const jLabel = formatJudgementLabel(assertion.judgement);
    judgementMap.set(jLabel, (judgementMap.get(jLabel) || 0) + 1);
    reviewedBallMap.set(
      reviewedBallLabel(assertion),
      (reviewedBallMap.get(reviewedBallLabel(assertion)) || 0) + 1
    );
    reviewedSanctionMap.set(
      reviewedSanctionLabel(assertion),
      (reviewedSanctionMap.get(reviewedSanctionLabel(assertion)) || 0) + 1
    );
    const effective = formatAssertionOutcome(assertion);
    effectiveMap.set(effective, (effectiveMap.get(effective) || 0) + 1);
    const sig = assertionDecisionSignature(assertion);
    signatureMap.set(sig, (signatureMap.get(sig) || 0) + 1);
  }

  const toSorted = (map: Map<string, number>) =>
    [...map.entries()].sort((a, b) => b[1] - a[1]).map(([label, count]) => ({ label, count }));

  return {
    ball: toSorted(ballMap),
    sanction: toSorted(sanctionMap),
    judgement: toSorted(judgementMap),
    reviewedBall: toSorted(reviewedBallMap),
    reviewedSanction: toSorted(reviewedSanctionMap),
    effective: toSorted(effectiveMap),
    signature: toSorted(signatureMap),
  };
}

/** Combined on-field ball + sanction counts (e.g. 自由球 · 黃牌: 2). */
export function onFieldDecisionDistributions(cluster: ConsolidatedEventCluster) {
  const map = new Map<string, number>();
  for (const assertion of cluster.assertions) {
    const sanction = normalizeSanction(assertion.onFieldSanction);
    const label = formatDecisionSummary(
      assertion.onFieldBall,
      sanction === 'no_penalty' ? null : sanction
    );
    map.set(label, (map.get(label) || 0) + 1);
  }
  return [...map.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([label, count]) => ({ label, count }));
}

export function formatClusterOnFieldDecision(cluster: ConsolidatedEventCluster): string {
  const sanction = normalizeSanction(cluster.dominantSanction);
  return formatDecisionSummary(cluster.onFieldBall, sanction === 'no_penalty' ? null : sanction);
}

export type ConsensusLeaderboardRow = {
  userId: string;
  userName: string;
  /** All participant marks across events. */
  totalMarks: number;
  /** Marks on events where a designated official also marked. */
  officialEventMarks: number;
  alignmentScore: number;
  judgementRate: number;
  reviewedBallRate: number;
  reviewedSanctionRate: number;
};

type LeaderboardAccumulator = {
  userName: string;
  totalMarks: number;
  officialEventMarks: number;
  componentAligned: Record<ReviewConsensusComponentKey, number>;
};

function emptyLeaderboardRow(userName: string): LeaderboardAccumulator {
  return {
    userName,
    totalMarks: 0,
    officialEventMarks: 0,
    componentAligned: { judgement: 0, reviewedBall: 0, reviewedSanction: 0 },
  };
}

/** Events at or below this agreement rate appear in 顯著爭議事件清單. */
export const SIGNIFICANT_DISCREPANCY_MAX_AGREEMENT_RATE = 60;

export function buildConsensusDashboard(
  clusters: ConsolidatedEventCluster[],
  officialUserIds?: number | number[]
) {
  const uniqueParticipants = new Set<string>();
  let varianceSum = 0;

  const leaderboard = new Map<string, LeaderboardAccumulator>();

  for (const cluster of clusters) {
    varianceSum += cluster.averageVariance || 0;
    const officials = getClusterOfficialAssertions(cluster, officialUserIds);
    for (const assertion of cluster.assertions) {
      if (assertion.role === 'participant') uniqueParticipants.add(assertion.userId);
      if (assertion.role !== 'participant') continue;

      const row = leaderboard.get(assertion.userId) ?? emptyLeaderboardRow(assertion.userName);
      row.totalMarks += 1;
      if (officials.length === 0) {
        leaderboard.set(assertion.userId, row);
        continue;
      }

      row.officialEventMarks += 1;
      const bestOfficial =
        officials.length === 1
          ? officials[0]
          : officials.reduce((best, o) =>
              assertionAlignmentScore(assertion, o) > assertionAlignmentScore(assertion, best)
                ? o
                : best
            , officials[0]);
      const match = assertionComponentsMatch(assertion, bestOfficial);
      for (const key of REVIEW_CONSENSUS_COMPONENT_KEYS) {
        if (match[key]) row.componentAligned[key] += 1;
      }
      leaderboard.set(assertion.userId, row);
    }
  }

  const clusterRates = clusters
    .map((c) => c.agreementRate)
    .filter((r): r is number => r != null && !Number.isNaN(r));

  const leaderboardRows: ConsensusLeaderboardRow[] = [...leaderboard.entries()]
    .map(([userId, row]) => {
      const denom = row.officialEventMarks;
      const rate = (key: ReviewConsensusComponentKey) =>
        denom ? (row.componentAligned[key] / denom) * 100 : 0;
      const judgementRate = rate('judgement');
      const reviewedBallRate = rate('reviewedBall');
      const reviewedSanctionRate = rate('reviewedSanction');
      const alignmentScore = denom
        ? (judgementRate + reviewedBallRate + reviewedSanctionRate) /
          REVIEW_CONSENSUS_COMPONENT_COUNT
        : 0;
      return {
        userId,
        userName: row.userName,
        totalMarks: row.totalMarks,
        officialEventMarks: row.officialEventMarks,
        alignmentScore,
        judgementRate,
        reviewedBallRate,
        reviewedSanctionRate,
      };
    })
    .sort((a, b) => b.alignmentScore - a.alignmentScore || b.totalMarks - a.totalMarks);

  const discrepancyClusters = clusters
    .filter(
      (x) =>
        x.agreementRate != null &&
        x.agreementRate <= SIGNIFICANT_DISCREPANCY_MAX_AGREEMENT_RATE
    )
    .sort((a, b) => (a.agreementRate ?? 0) - (b.agreementRate ?? 0));

  const systemAgreementRate = clusterRates.length
    ? clusterRates.reduce((s, r) => s + r, 0) / clusterRates.length
    : 0;

  return {
    totalClusters: clusters.length,
    totalParticipants: uniqueParticipants.size,
    averageVariance: clusters.length ? varianceSum / clusters.length : 0,
    systemAgreementRate,
    /** Average per-event 共識% (same as systemAgreementRate). */
    averageClusterAgreement: systemAgreementRate,
    leaderboardRows,
    discrepancyClusters,
  };
}

/** Whether the participant's mark matches official in this cluster; null if no participant mark */
export function isParticipantAlignedWithOwner(
  cluster: ConsolidatedEventCluster,
  userId: string | number,
  officialUserId?: number | number[]
): boolean | null {
  const officials = getClusterOfficialAssertions(cluster, officialUserId);
  if (officials.length === 0) return null;
  const uid = String(userId);
  const mine = cluster.assertions.filter((a) => a.role === 'participant' && a.userId === uid);
  if (mine.length === 0) return null;
  return mine.some((a) => isAssertionAlignedWithOfficials(a, officials));
}

export function getParticipantOfficialAlignment(
  clusters: ConsolidatedEventCluster[],
  userId: string | number,
  officialUserId?: number | number[]
) {
  const uid = String(userId);
  let officialEvents = 0;
  let myMarked = 0;
  let aligned = 0;
  let varianceSum = 0;
  let varianceCount = 0;

  for (const cluster of clusters) {
    const officials = getClusterOfficialAssertions(cluster, officialUserId);
    if (officials.length === 0) continue;
    officialEvents += 1;
    const mine = cluster.assertions.filter((a) => a.role === 'participant' && a.userId === uid);
    if (mine.length > 0) {
      myMarked += 1;
      if (mine.some((a) => isAssertionAlignedWithOfficials(a, officials))) aligned += 1;
      varianceSum += cluster.averageVariance || 0;
      varianceCount += 1;
    }
  }

  return {
    officialEvents,
    myMarked,
    aligned,
    alignmentRate: myMarked > 0 ? (aligned / myMarked) * 100 : 0,
    averageVariance: varianceCount > 0 ? varianceSum / varianceCount : 0,
    missedEvents: officialEvents - myMarked,
  };
}

export function getParticipantDiscrepancyClusters(
  clusters: ConsolidatedEventCluster[],
  userId: string | number,
  officialUserId?: number | number[]
): ConsolidatedEventCluster[] {
  const uid = String(userId);
  return clusters.filter(
    (cluster) => isParticipantAlignedWithOwner(cluster, uid, officialUserId) === false
  );
}
